import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  DollarSign,
  Eye,
  Download,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
  Copy
} from 'lucide-react';
import { formatCurrency, formatDate, formatCompactCurrency } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

const TransactionHistory = () => {
  const navigate = useNavigate();
  const { getDisplayName } = useAuth();
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filters, setFilters] = useState({
    bondFilter: 'all',
    transactionType: 'all',
    dateRange: {
      startDate: '',
      endDate: ''
    }
  });

  // Mock transaction data
  const mockTransactions = [
    {
      id: 1,
      type: 'payout_received',
      bondName: 'Green Sukuk Infrastructure 2027',
      amount: 1250,
      date: '2024-01-15',
      transactionHash: '0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef',
      status: 'completed',
      blockNumber: 18456789,
      gasUsed: 21000,
      gasPrice: '20 gwei'
    },
    {
      id: 2,
      type: 'token_received',
      bondName: 'Sukuk 2024-B',
      amount: 25000,
      date: '2024-01-10',
      transactionHash: '0xabcdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890',
      status: 'completed',
      blockNumber: 18456700,
      gasUsed: 65000,
      gasPrice: '18 gwei'
    },
    {
      id: 3,
      type: 'payout_received',
      bondName: 'Green Sukuk Infrastructure 2027',
      amount: 625,
      date: '2024-01-01',
      transactionHash: '0x9876543210fedcba9876543210fedcba9876543210fedcba9876543210fedcba',
      status: 'completed',
      blockNumber: 18456650,
      gasUsed: 21000,
      gasPrice: '22 gwei'
    },
    {
      id: 4,
      type: 'token_received',
      bondName: 'Green Sukuk Infrastructure 2027',
      amount: 50000,
      date: '2023-12-15',
      transactionHash: '0xfedcba0987654321fedcba0987654321fedcba0987654321fedcba0987654321',
      status: 'completed',
      blockNumber: 18456500,
      gasUsed: 65000,
      gasPrice: '15 gwei'
    }
  ];

  const mockPerformanceData = {
    totalInvested: 75000,
    totalPayouts: 1875,
    totalValue: 78750,
    totalReturn: 5.0,
    averageYield: 4.2,
    numberOfTransactions: 4,
    activeBonds: 2
  };

  const bondOptions = [
    { value: 'all', label: 'All Bonds' },
    { value: 'green_sukuk', label: 'Green Sukuk Infrastructure 2027' },
    { value: 'islamic_bond', label: 'Sukuk 2024-B' }
  ];

  const transactionTypes = [
    { value: 'all', label: 'All Transactions' },
    { value: 'payout_received', label: 'Payout Received' },
    { value: 'token_received', label: 'Token Received' },
    { value: 'token_sent', label: 'Token Sent' }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { label: 'Completed', class: 'badge-success' },
      pending: { label: 'Pending', class: 'badge-warning' },
      failed: { label: 'Failed', class: 'badge-error' }
    };
    const config = statusConfig[status] || { label: status, class: 'badge-secondary' };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const getTransactionTypeIcon = (type) => {
    switch (type) {
      case 'payout_received':
        return <DollarSign className="h-4 w-4 text-success-600" />;
      case 'token_received':
        return <CheckCircle className="h-4 w-4 text-info-600" />;
      case 'token_sent':
        return <XCircle className="h-4 w-4 text-error-600" />;
      default:
        return <Clock className="h-4 w-4 text-secondary-600" />;
    }
  };

  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case 'payout_received':
        return 'Payout Received';
      case 'token_received':
        return 'Token Received';
      case 'token_sent':
        return 'Token Sent';
      default:
        return type;
    }
  };

  const filteredTransactions = mockTransactions.filter(transaction => {
    if (filters.bondFilter !== 'all' && transaction.bondName !== filters.bondFilter) {
      return false;
    }
    if (filters.transactionType !== 'all' && transaction.type !== filters.transactionType) {
      return false;
    }
    if (filters.dateRange.startDate && new Date(transaction.date) < new Date(filters.dateRange.startDate)) {
      return false;
    }
    if (filters.dateRange.endDate && new Date(transaction.date) > new Date(filters.dateRange.endDate)) {
      return false;
    }
    return true;
  });

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetails(true);
  };

  const handleExport = () => {
    // Simulate export functionality
    console.log('Exporting transaction history...');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="shadow bg-white py-4 px-8">
        <h1 className="text-2xl font-bold text-gray-900">Transaction History</h1>
        <p className="text-secondary-600 mt-2">
          Welcome, {getDisplayName()}! Monitor your investment performance and track all transactions.
        </p>
      </div>

      {/* Performance Summary */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="card">
          <div className="card-body text-center">
            <TrendingUp className="h-8 w-8 text-success-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary-900">
              {formatCompactCurrency(mockPerformanceData.totalValue)}
            </div>
            <div className="text-sm text-secondary-600">Total Portfolio Value</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <DollarSign className="h-8 w-8 text-info-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary-900">
              {formatCompactCurrency(mockPerformanceData.totalPayouts)}
            </div>
            <div className="text-sm text-secondary-600">Total Payouts</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <BarChart3 className="h-8 w-8 text-primary-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary-900">
              {mockPerformanceData.totalReturn}%
            </div>
            <div className="text-sm text-secondary-600">Total Return</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <Clock className="h-8 w-8 text-warning-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary-900">
              {mockPerformanceData.numberOfTransactions}
            </div>
            <div className="text-sm text-secondary-600">Total Transactions</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="card-header">
          <h3>Filters</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Bond Filter
              </label>
              <select
                className="select select-bordered w-full"
                value={filters.bondFilter}
                onChange={(e) => setFilters(prev => ({ ...prev, bondFilter: e.target.value }))}
              >
                {bondOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Transaction Type
              </label>
              <select
                className="select select-bordered w-full"
                value={filters.transactionType}
                onChange={(e) => setFilters(prev => ({ ...prev, transactionType: e.target.value }))}
              >
                {transactionTypes.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                className="input input-bordered w-full"
                value={filters.dateRange.startDate}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  dateRange: { ...prev.dateRange, startDate: e.target.value }
                }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                className="input input-bordered w-full"
                value={filters.dateRange.endDate}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  dateRange: { ...prev.dateRange, endDate: e.target.value }
                }))}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card">
        <div className="card-header">
          <h3>Transaction History</h3>
          <button
            onClick={handleExport}
            className="btn btn-outline btn-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Type</th>
                  <th className="table-header-cell">Bond</th>
                  <th className="table-header-cell">Amount</th>
                  <th className="table-header-cell">Date</th>
                  <th className="table-header-cell">Transaction Hash</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="border-b border-secondary-100 hover:bg-secondary-50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        {getTransactionTypeIcon(transaction.type)}
                        <span className="ml-2 text-sm text-secondary-600">
                          {getTransactionTypeLabel(transaction.type)}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-secondary-600 text-sm">
                      {transaction.bondName}
                    </td>
                    <td className="py-4 px-4 text-secondary-600 text-sm">
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="py-4 px-4 text-secondary-600 text-sm">
                      {formatDate(transaction.date)}
                    </td>
                    <td className="py-4 px-4 text-secondary-600 text-sm font-mono">
                      {transaction.transactionHash.substring(0, 10)}...
                    </td>
                    <td className="py-4 px-4">
                      {getStatusBadge(transaction.status)}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <button
                        onClick={() => handleViewDetails(transaction)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-secondary-100 hover:bg-secondary-200 text-secondary-600 hover:text-secondary-700 transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Performance Chart */}
      <div className="card">
        <div className="card-header">
          <h3>Performance Overview</h3>
        </div>
        <div className="card-body">
          <div className="h-64 bg-secondary-50 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <BarChart3 className="h-12 w-12 text-secondary-400 mx-auto mb-2" />
              <p className="text-sm text-secondary-600">Performance chart will be displayed here</p>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      {showDetails && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Transaction Details</h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700">Transaction Type:</label>
                  <div className="flex items-center mt-1">
                    {getTransactionTypeIcon(selectedTransaction.type)}
                    <span className="ml-2 text-sm text-secondary-900">
                      {getTransactionTypeLabel(selectedTransaction.type)}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Amount:</label>
                  <div className="text-sm text-secondary-900 mt-1">
                    {formatCurrency(selectedTransaction.amount)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Bond:</label>
                  <div className="text-sm text-secondary-900 mt-1">
                    {selectedTransaction.bondName}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Date:</label>
                  <div className="text-sm text-secondary-900 mt-1">
                    {formatDate(selectedTransaction.date)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Status:</label>
                  <div className="mt-1">
                    {getStatusBadge(selectedTransaction.status)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Block Number:</label>
                  <div className="text-sm text-secondary-900 mt-1">
                    {selectedTransaction.blockNumber}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-secondary-700">Transaction Hash:</label>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="text-sm text-secondary-600 font-mono break-all flex-1">
                    {selectedTransaction.transactionHash}
                  </div>
                  <button
                    onClick={() => copyToClipboard(selectedTransaction.transactionHash)}
                    className="btn btn-ghost btn-xs"
                    title="Copy Hash"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => window.open(`https://etherscan.io/tx/${selectedTransaction.transactionHash}`, '_blank')}
                    className="btn btn-ghost btn-xs"
                    title="View on Etherscan"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700">Gas Used:</label>
                  <div className="text-sm text-secondary-900 mt-1">
                    {selectedTransaction.gasUsed.toLocaleString()}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Gas Price:</label>
                  <div className="text-sm text-secondary-900 mt-1">
                    {selectedTransaction.gasPrice}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;